import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ShieldAlert, CheckCheck, X, AlertTriangle } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const TYPE_ICON: Record<string, React.ElementType> = {
  risk_alert: ShieldAlert,
  integration_auth_error: AlertTriangle,
};

// Panel rendered at AppShell level so it escapes sidebar stacking contexts
export function NotificationPanel({ onClose, anchorRect }: { onClose: () => void; anchorRect: DOMRect | null }) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  function handleNotifClick(n: {
    notificationId: string;
    read: boolean;
    projectId: string | null;
    actionUrl?: string | null;
  }) {
    if (!n.read) markRead(n.notificationId);
    onClose();
    // actionUrl takes precedence — used by integration auth errors to deep-link
    // into Settings → Integrations rather than a project page.
    if (n.actionUrl) {
      navigate(n.actionUrl);
      return;
    }
    if (n.projectId) navigate(`/projects/${n.projectId}`);
  }

  const panelH = 480;
  const panelW = 360;
  // Anchor the panel to the button: open downward from the button's top when
  // there's room below, otherwise open upward from the button's bottom.
  // (The bell lives in the sidebar footer, near the bottom of the viewport,
  // so anchorRect.top is usually too close to window.innerHeight for the
  // panel to fit below it — clamping `top` down there alone would strand
  // the panel far from the button instead of flipping to open upward.)
  const spaceBelow = anchorRect ? window.innerHeight - anchorRect.top - 8 : panelH;
  const top = anchorRect
    ? spaceBelow >= panelH
      ? anchorRect.top
      : Math.max(8, anchorRect.bottom - panelH)
    : 80;
  const left = anchorRect
    ? Math.min(anchorRect.right + 8, window.innerWidth - panelW - 8)
    : 264;

  return (
    <>
      {/* Invisible backdrop — catches outside clicks */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        ref={panelRef}
        style={{ position: 'fixed', top, left, width: panelW, maxHeight: panelH, zIndex: 9999 }}
        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/[0.12] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/[0.06] flex-shrink-0">
          <span className="text-meta font-semibold text-gray-900 dark:text-white">Notifications</span>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-micro text-primary hover:text-primary/80 transition-colors"
              >
                <CheckCheck className="h-4 w-4" />
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-0.5 rounded"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          {notifications.length === 0 ? (
            <p className="text-center text-meta text-gray-400 dark:text-gray-500 py-10">
              No notifications yet
            </p>
          ) : (
            notifications.map((n) => {
              const Icon = TYPE_ICON[n.type] ?? Bell;
              return (
                <button
                  key={n.notificationId}
                  onClick={() => handleNotifClick(n)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors border-b border-gray-100 dark:border-white/[0.04] last:border-0 ${
                    !n.read ? 'bg-primary/[0.03] dark:bg-primary/[0.06]' : ''
                  }`}
                >
                  <div className={`mt-0.5 flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${
                    n.type === 'risk_alert'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-500'
                      : n.type === 'integration_auth_error'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                        : 'bg-gray-100 dark:bg-white/10 text-gray-500'
                  }`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-meta ${!n.read ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                      {n.title}
                    </p>
                    <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                      {n.body}
                    </p>
                    <p className="text-micro text-gray-400 dark:text-gray-500 mt-1">
                      {timeAgo(n.createdAt)}
                    </p>
                  </div>
                  {!n.read && (
                    <span className="mt-1.5 flex-shrink-0 h-2 w-2 rounded-full bg-primary" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

// Bell button — just triggers open, panel lives in AppShell
export function NotificationBell({ collapsed = false, onOpen }: { collapsed?: boolean; onOpen: (rect: DOMRect) => void }) {
  const { unreadCount } = useNotifications();
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(() => {
    if (btnRef.current) onOpen(btnRef.current.getBoundingClientRect());
  }, [onOpen]);

  return (
    <button
      ref={btnRef}
      onClick={handleClick}
      title="Notifications"
      className={`relative flex items-center gap-3 w-full px-3 py-2 rounded-xl text-meta text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all ${
        collapsed ? 'justify-center' : ''
      }`}
    >
      {/* Expanded rows put the count at the END of the row (no occlusion at
          any width); only the collapsed icon-only state wears a corner badge.
          The corner badge is anchored by its LEFT edge past the icon's
          center so multi-digit counts grow rightward, away from the bell,
          instead of covering it. */}
      <div className="relative flex-shrink-0">
        <Bell className="h-4 w-4" />
        {collapsed && unreadCount > 0 && (
          <span className="absolute -top-2 left-2 h-4 min-w-4 px-1 bg-primary rounded-full flex items-center justify-center text-micro font-bold text-white leading-none tabular-nums">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>
      {!collapsed && (
        <>
          <span>Notifications</span>
          {unreadCount > 0 && (
            <span className="ml-auto h-4 min-w-4 px-1 bg-primary rounded-full flex items-center justify-center text-micro font-bold text-white leading-none tabular-nums">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </>
      )}
    </button>
  );
}
